// dependencies
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import moment from "moment";

// components
import Spinner from "Components/Spinner/PageSpinner";
import Table from "Components/TableLite";
import Button from "Components/Button";
import { useSelector } from "react-redux";

// api
import { getEngagementLogs } from "Api/services/company";
import { getCurrentEnvironment } from "Selectors/app";

// css
import css from "./EngagementLogs.styl";

const EngagementLogs = ({ companyID }) => {
	const [logs, setLogs] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState(null);
	const [selectedLog, setSelectedLog] = useState(null);
	const { name: env } = useSelector(getCurrentEnvironment);

	useEffect(() => {
		const fetchLogs = async () => {
			try {
				setIsLoading(true);
				const response = await getEngagementLogs({ companyID, env });

				if (response.success) {
					setLogs(response.engagementLogs || []);
				} else {
					setError("Failed to load engagement logs");
				}
			} catch (err) {
				setError("An error occurred while fetching engagement logs");
			} finally {
				setIsLoading(false);
			}
		};

		fetchLogs();
	}, [companyID, env]);

	const handleViewClick = (log) => {
		setSelectedLog(log);
	};

	const handleBackClick = () => {
		setSelectedLog(null);
	};

	// Define the columns for the TableLite component
	const columns = [
		{
			id: "name",
			label: "NAME",
			width: "1fr",
		},
		{
			id: "time",
			label: "TIME",
			width: "1fr",
		},
		{
			id: "action",
			label: "Action",
			width: "1fr",
		},
		{
			id: "userAction",
			label: "",
			width: "0.25fr",
		},
	];

	const actionMap = {
		update: "Updated Engagement Settings",
		manual_send_reminder_emails: "Manual Reminder Sent",
		manual_close_survey: "Survey Closed",
	};

	// Transform logs data to rows format expected by TableLite
	const rows = logs.map((log, index) => {
		const userName = log.userName || "System";
		const formattedDate = moment(log.created_at).format("M/D/YYYY h:mm A");
		const action = actionMap[log.change_type];

		return {
			id: log._id || `log-${index}`,
			name: userName,
			time: formattedDate,
			action,
			userAction:
				action === actionMap.update ? (
					<a
						href="#"
						className={css["view-link"]}
						onClick={(e) => {
							e.preventDefault();
							handleViewClick(log);
						}}
					>
						View
					</a>
				) : null,
			_original: log,
		};
	});

	// Define columns for change details view
	const changeDetailsColumns = [
		{
			id: "field",
			label: "FIELD",
			width: "1fr",
		},
		{
			id: "previousValue",
			label: "PREVIOUS VALUE",
			width: "1fr",
		},
		{
			id: "newValue",
			label: "NEW VALUE",
			width: "1fr",
		},
	];

	// Extract changes from the logs for the details view
	const getChangeDetailsRows = (selectedLog) => {
		// Get all keys from both states
		const newState = selectedLog.new_state || {};
		const previousState = selectedLog.previous_state || {};

		// Collect all engagement-related keys
		const allKeys = new Set();

		// Function to extract all engagement keys from an object
		const extractEngagementKeys = (obj) => {
			Object.keys(obj).forEach((key) => {
				if (key.startsWith("engagement.")) {
					allKeys.add(key);
				}
			});
		};

		extractEngagementKeys(newState);
		extractEngagementKeys(previousState);

		// Map of technical field names to display names
		const fieldDisplayNames = {
			"engagement.enabled": "Surveys Enabled",
			"engagement.frequency": "Survey Frequency",
			"engagement.enps_frequency": "Ask eNPS Questions",
			"engagement.ask_additional_enps_feedback_question": "Ask Additional eNPS Feedback",
			"engagement.factors_frequency": "Survey Frequency",
			"engagement.next_survey_date": "Next Survey Date",
			"engagement.last_survey_date": "Last Survey Date",
			"engagement.survey_time": "Preferred Survey Time",
			"engagement.question_cycle": "Question Cycle",
			"engagement.custom_questions_frequency": "Ask Custom Questions",
			"engagement.custom_questions_include_all": "Include All Custom Questions",
			"engagement.send_reminder_emails": "Send Reminders",
			"engagement.additional_feedback_question_frequency": "Ask Additional Feedback Question",
			"engagement.employee_status_for_surveys": "Employee Status for Surveys",
		};

		// Helper function to format values for display
		const formatValue = (key, value) => {
			if (value === undefined || value === null) return "Not set";

			if (typeof value === "boolean") return value ? "True" : "False";

			// Format dates
			if (key.includes("date") && typeof value === "string" && value.includes("T")) {
				return moment(value).format("MMMM D YYYY");
			}

			// Format specific fields
			switch (key) {
				case "engagement.factors_frequency":
					const factorsFrequencyMap = {
						1: "Every Survey",
						2: "Every 2nd Survey",
						3: "Every 3rd Survey",
						4: "Every 4th Survey",
						0: "Never",
					};
					return factorsFrequencyMap[value] || value;

				case "engagement.enps_frequency":
					const enpsFrequencyMap = {
						1: "Every Survey",
						2: "Every 2nd Survey",
						3: "Every 3rd Survey",
						4: "Every 4th Survey",
						0: "Never",
					};
					return enpsFrequencyMap[value] || value;

				case "engagement.frequency":
					// Survey frequency is in days, convert to appropriate format
					if (value === 14) return "Every 2 Weeks";
					if (value === 21) return "Every 3 Weeks";
					if (value === 28) return "Every 4 Weeks";
					if (value === 91) return "Every 3 Months";
					if (value === 182) return "Every 6 Months";
					if (value === 364) return "Every 12 Months";
					// Handle custom day values
					if (value === 7) return "Every Week";
					if (value === 1) return "Every Day";
					return `Every ${value} Days`;

				case "engagement.survey_time":
					const surveyTimeMap = {
						morning: "Morning",
						"mid-day": "Mid-day",
						afternoon: "Afternoon",
						evening: "Evening",
					};
					return surveyTimeMap[value] || value;

				case "engagement.employee_status_for_surveys":
					const statusMap = {
						active: "Active",
						invited: "Invited",
						all: "All",
					};
					return statusMap[value] || value;

				case "engagement.custom_questions_frequency":
					const customQuestionsFrequencyMap = {
						1: "Every Survey",
						2: "Every 2nd Survey",
						3: "Every 3rd Survey",
						4: "Every 4th Survey",
						0: "Never",
					};
					return customQuestionsFrequencyMap[value] || value;

				case "engagement.additional_feedback_question_frequency":
					const additionalFeedbackFrequencyMap = {
						1: "Every Survey",
						2: "Every 2nd Survey",
						3: "Every 3rd Survey",
						4: "Every 4th Survey",
						0: "Never",
					};
					return additionalFeedbackFrequencyMap[value] || value;

				default:
					return String(value);
			}
		};

		// Create rows for each changed field
		const changeRows = [];

		allKeys.forEach((key) => {
			const newValue = newState[key];
			const previousValue = previousState[key];

			// Only add row if values are different
			if (JSON.stringify(newValue) !== JSON.stringify(previousValue)) {
				// Extract the field name without the 'engagement.' prefix for display
				const fieldName = key.replace("engagement.", "");

				changeRows.push({
					id: key,
					field: fieldDisplayNames[key] || fieldName,
					previousValue: formatValue(key, previousValue),
					newValue: formatValue(key, newValue),
				});
			}
		});

		return changeRows;
	};

	if (isLoading && logs.length === 0) {
		return <Spinner />;
	}

	// If a log is selected, show the detailed view
	if (selectedLog) {
		const changeRows = getChangeDetailsRows(selectedLog);
		const userName = selectedLog.userName || "System";
		const changeDate = moment(selectedLog.created_at).format("M/D/YYYY h:mm A");

		return (
			<div className={css["engagement-changes-view"]}>
				<div className={css["back-button-container"]}>
					<Button type="text" icon="arrow_back" onClick={handleBackClick} className={css["back-button"]}>
						Back to Logs
					</Button>
				</div>

				<div className={css["change-header"]}>
					<p className={css["change-description"]}>
						"This page lists all fields that were changed when a user saved updates on the Pulse Survey
						settings page. Fields that were not changed are not shown."
					</p>
					<p className={css["change-author"]}>Change by: {userName}</p>
					<p className={css["change-date"]}>Change on: {changeDate}</p>
				</div>

				{changeRows.length > 0 ? (
					<Table
						columns={changeDetailsColumns}
						rows={changeRows}
						bodyCellClass={css["details-table-cell"]}
						headCellClass={css["details-table-header-cell"]}
					/>
				) : (
					<div className={css["no-changes-message"]}>No changes were detected in this log entry.</div>
				)}
			</div>
		);
	}

	// Show the list of logs (default view)
	return (
		<div className={css["engagement-settings-changelogs"]}>
			<h2>Engagement Settings Changelogs</h2>

			{error && <div className={css["engagement-logs-error-message"]}>{error}</div>}

			{!error && logs.length === 0 ? (
				<div className={css["engagement-logs-empty-state"]}>No engagement logs found</div>
			) : (
				<Table
					columns={columns}
					rows={rows}
					isLoading={isLoading}
					bodyCellClass={css["table-cell"]}
					headCellClass={css["table-header-cell"]}
				/>
			)}
		</div>
	);
};

EngagementLogs.propTypes = {
	companyID: PropTypes.string.isRequired,
};

export default EngagementLogs;
